import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import Utils from "Utils/utils";
import {
  BULK_TASK_TYPE,
  UPDATE_TASK_READ_STATUS_LABEL
} from "Model/model";
import bulkJobsStore, { JOB_FILTER, BULK_NOTIFICATION_OPEN_STATUSES } from "Stores/BulkJobsStore";
import { ReactComponent as BjnBellIcon } from "Assets/svg/bjnBell.svg";
import { ReactComponent as BjnCloseIcon } from "Assets/svg/bjnClose.svg";
import { ReactComponent as BjnFilterIcon } from "Assets/svg/bjnFilter.svg";
import { ReactComponent as BjnChevronDownIcon } from "Assets/svg/bjnChevronDown.svg";
import { ReactComponent as BjnCheckmarkIcon } from "Assets/svg/bjnCheckmark.svg";
import { ReactComponent as BjnArrowRightIcon } from "Assets/svg/bjnArrowRight.svg";
import { ReactComponent as BjnBulkActionIcon } from "Assets/svg/bjnBulkAction.svg";
import { ReactComponent as BjnCsvUploadIcon } from "Assets/svg/bjnCsvUpload.svg";
import { ReactComponent as BjnLinkedInIcon } from "Assets/svg/bjnLinkedIn.svg";
import { ReactComponent as BjnDataExportIcon } from "Assets/svg/bjnDataExport.svg";
import { ReactComponent as BjnEnrichmentIcon } from "Assets/svg/bjnEnrichment.svg";
import BulkJobNotificationLoader from "./BulkJobNotificationLoader";

const STATUS_LABELS = {
  TO_BE_PROCESSED: "Pending",
  IN_PROGRESS: "In Progress",
  PROCESSED: "Completed",
  FAILED: "Failed"
};

const STATUS_CLASS = {
  TO_BE_PROCESSED: "queued",
  IN_PROGRESS: "in_progress",
  PROCESSED: "processed",
  FAILED: "failed"
};

const CLOSED_POLL_INTERVAL = 30000;
const OPEN_POLL_INTERVAL = 10000;

const FILTERS = Object.values(JOB_FILTER);

const FILTER_TYPE_MAP = {
  [JOB_FILTER.BULK_ACTIONS]: "bulk_action",
  [JOB_FILTER.CSV_UPLOAD]: "csv_upload",
  [JOB_FILTER.LINKEDIN_IMPORT]: "linkedin_extract",
  [JOB_FILTER.ENRICHMENT]: "enrichment"
};

const CATEGORY_ROUTE_MAP = {
  csv_upload: "/settings/imports/csv",
  linkedin_extract: "/settings/imports/linkedin",
  data_export: "/settings/imports/exports",
  enrichment: "/settings/imports/enrich",
  bulk_action: "/settings/imports/bulk-actions"
};

const TYPE_META = {
  bulk_action: { label: "Bulk Actions", icon: <BjnBulkActionIcon /> },
  csv_upload: { label: "CSV Upload", icon: <BjnCsvUploadIcon /> },
  linkedin_extract: { label: "LinkedIn Import", icon: <BjnLinkedInIcon /> },
  data_export: { label: "CSV Export", icon: <BjnDataExportIcon /> },
  enrichment: { label: "Enrichment", icon: <BjnEnrichmentIcon /> }
};

const getJobCategory = (type = "") => {
  if (type === "ENRICH_CONTACT") return "enrichment";
  if (type === "CSV_UPLOAD") return "csv_upload";
  if (type === "LINKEDIN_EXTRACT") return "linkedin_extract";
  if (type === "DATA_EXPORT") return "data_export";
  return "bulk_action";
};

const MAX_VISIBLE_JOBS = 5;

const SYNTHETIC_TYPE_LABELS = {
  CSV_UPLOAD: "CSV Upload",
  LINKEDIN_EXTRACT: "LinkedIn Extract-SDRs",
  DATA_EXPORT: "CSV Export"
};

const getJobLabel = (job = {}) => {
  if (job?.type === "UPDATE_TASK_READ_STATUS") {
    return (
      UPDATE_TASK_READ_STATUS_LABEL[job?.readUnreadJobType || ""] ||
      "Update Read Status"
    );
  }
  return BULK_TASK_TYPE[job?.type] || SYNTHETIC_TYPE_LABELS[job?.type] || job?.type || "Bulk Action";
};

const getProgressValues = (job = {}) => {
  const {
    totalContacts = 0,
    processedContacts = 0,
    totalCount = 0,
    successCount = 0,
    status = ""
  } = job || {};

  const isOpen = BULK_NOTIFICATION_OPEN_STATUSES.includes(status);
  const total = totalCount > 0 ? totalCount : totalContacts;
  const processed = isOpen ? processedContacts : successCount;
  return { total, processed };
};

const formatJobDate = createTs => {
  if (!createTs) return "--";
  try {
    return new Date(createTs).toLocaleString([], {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  } catch {
    return Utils.getDateFromString(createTs);
  }
};

const JobProgressBar = ({ total = 0, processed = 0 }) => {
  const pct = total > 0 ? Math.min(Math.round((processed / total) * 100), 100) : 0;
  return (
    <div className="bulkJobInlineBarWrapper">
      <div className="bulkJobInlineBar">
        <div className="bulkJobInlineBarFill" style={{ width: `${pct}%` }} />
      </div>
      <span className="bulkJobInlineBarCount">
        {processed.toLocaleString()}/{total.toLocaleString()}
      </span>
    </div>
  );
};

const BulkJobNotification = observer(() => {
  const history = useHistory();
  const wrapperRef = useRef(null);
  const activeFilterRef = useRef("All");
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSwitching, setFilterSwitching] = useState(false);

  const {
    jobs = [],
    loading = false,
    guideVisible = false,
    highlightActive = false,
    guideMessage = ""
  } = bulkJobsStore;

  const inProgressCount = jobs.filter(j => j?.status === "IN_PROGRESS")?.length ?? 0;

  const filteredJobs = (activeFilter === JOB_FILTER.ALL
    ? jobs
    : jobs.filter(j => getJobCategory(j?.type) === FILTER_TYPE_MAP[activeFilter])
  ).slice(0, MAX_VISIBLE_JOBS);

  const closeDropdown = () => {
    setShowDropdown(false);
    setFilterOpen(false);
  };

  const handleBellClick = () => {
    bulkJobsStore.dismissGuide();
    const nextValue = !showDropdown;
    setShowDropdown(nextValue);
    if (nextValue) {
      bulkJobsStore.fetch(JOB_FILTER.ALL);
    } else {
      setFilterOpen(false);
    }
  };

  const handleViewAll = () => {
    closeDropdown();
    history.push("/settings/imports/bulk-actions");
  };

  useEffect(() => {
    bulkJobsStore.fetch(JOB_FILTER.ALL);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden && bulkJobsStore.openJobsCount > 0) {
        bulkJobsStore.fetch(activeFilterRef.current);
      }
    }, showDropdown ? OPEN_POLL_INTERVAL : CLOSED_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [showDropdown]);

  useEffect(() => {
    if (!loading) setFilterSwitching(false);
  }, [loading]);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (!wrapperRef.current?.contains(event.target)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);


  return (
    <div
      className={`bulkJobsNotificationWrapper${highlightActive ? " highlightActive" : ""}`}
      ref={wrapperRef}
    >
      {/* Bell button */}
      <button
        type="button"
        className={`bulkJobsNotificationButton${showDropdown ? " dropdownOpen" : ""}`}
        title="Job notifications"
        onClick={handleBellClick}
      >
        <BjnBellIcon />
        {inProgressCount > 0 && (
          <span className="bulkJobsNotificationBadge">{inProgressCount}</span>
        )}
      </button>

      {/* Guide tooltip */}
      {guideVisible && (
        <div className="bulkJobsGuideTooltip">
          <span className="guideDot" />
          <span>{guideMessage}</span>
        </div>
      )}

      {showDropdown && (
        <div className="bulkJobsDropdown">
          <div className="bulkJobsDropdownHeader">
            <div className="bulkJobsDropdownTitleRow">
              <div className="bulkJobsDropdownTitleGroup">
                <span className="bulkJobsDropdownTitle">Jobs</span>
                {inProgressCount > 0 && (
                  <span className="bulkJobsRunningBadge">
                    <span className="bulkJobsRunningDot" />
                    {inProgressCount} running
                  </span>
                )}
              </div>
              <button
                type="button"
                className="bulkJobCloseBtn"
                onClick={closeDropdown}
              >
                <BjnCloseIcon />
              </button>
            </div>

            <div className="bulkJobFilterWrapper">
              <button
                type="button"
                className={`bulkJobFilterBtn${activeFilter !== JOB_FILTER.ALL ? " active" : ""}`}
                onClick={() => setFilterOpen(o => !o)}
              >
                <BjnFilterIcon />
                {activeFilter === JOB_FILTER.ALL ? "All types" : activeFilter}
                <BjnChevronDownIcon className={`bulkJobFilterChevron${filterOpen ? " open" : ""}`} />
              </button>
              {filterOpen && (
                <div className="bulkJobFilterDropdown">
                  {FILTERS.map(f => (
                    <button
                      key={f}
                      type="button"
                      className={`bulkJobFilterOption${activeFilter === f ? " active" : ""}`}
                      onClick={() => {
                        setActiveFilter(f);
                        activeFilterRef.current = f;
                        setFilterOpen(false);
                        setFilterSwitching(true);
                        bulkJobsStore.fetch(f);
                      }}
                    >
                      {f}
                      {activeFilter === f && <BjnCheckmarkIcon />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bulkJobsDropdownBody">
            {filterSwitching ? (
              <BulkJobNotificationLoader />
            ) : filteredJobs?.length > 0 ? (
              filteredJobs.map(job => {
                const category = getJobCategory(job?.type);
                const typeMeta = TYPE_META[category] || TYPE_META.bulk_action;
                const statusClass = STATUS_CLASS[job?.status] || "queued";
                const isInProgress = job?.status === "IN_PROGRESS";
                const { total, processed } = getProgressValues(job);
                return (
                  <button
                    type="button"
                    className="bulkJobItem"
                    key={job?.id}
                    onClick={() => { closeDropdown(); history.push(CATEGORY_ROUTE_MAP[category] || "/settings/imports/bulk-actions"); }}
                  >
                    <div className="bulkJobTypeIcon">{typeMeta.icon}</div>
                    <div className="bulkJobItemContent">
                      <div className="bulkJobItemTopRow">
                        <span className="bulkJobItemTitle">{getJobLabel(job)}</span>
                        <span className={`bulkJobStatus ${statusClass}`}>
                          <span className={`bulkJobStatusDot${isInProgress ? " pulse" : ""}`} />
                          {STATUS_LABELS[job?.status] || "--"}
                        </span>
                      </div>
                      <div className="bulkJobItemMetaRow">
                        <span className="bulkJobTypeChip">{typeMeta.label}</span>
                        <span className="bulkJobMetaDot">·</span>
                        <span className="bulkJobCreatedAt">{formatJobDate(job?.createTs)}</span>
                      </div>
                      <JobProgressBar total={total} processed={processed} />
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="bulkJobsEmptyState">No jobs found.</div>
            )}
          </div>

          {/* Footer */}
          <button
            type="button"
            className="bulkJobsViewAllBtn"
            onClick={handleViewAll}
          >
            View all jobs
            <BjnArrowRightIcon />
          </button>
        </div>
      )}
    </div>
  );
});

export default BulkJobNotification;
